import { api } from './api'
import type { AuthResponse, User, GoogleUserInfo } from '@/types/auth'

declare global {
  interface Window {
    google: any
    gapi: any
  }
}

class AuthService {
  private googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
  private redirectUri = `${import.meta.env.VITE_APP_URL}/auth/callback`
  private isGoogleInitialized = false

  async initGoogleAuth(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Verificar se já foi inicializado
      if (this.isGoogleInitialized) {
        resolve()
        return
      }

      // Aguardar script do Google carregar
      const checkGoogle = () => {
        if (window.google?.accounts?.oauth2) {
          try {
            this.isGoogleInitialized = true
            resolve()
          } catch (error) {
            console.error('Google Auth initialization error:', error)
            reject(error)
          }
        } else {
          // Tentar novamente em 100ms
          setTimeout(checkGoogle, 100)
        }
      }

      checkGoogle()
    })
  }

  /**
   * Inicia o fluxo de autenticação OAuth2 com redirect
   */
  async startGoogleAuth(): Promise<void> {
    if (!this.isGoogleInitialized) {
      await this.initGoogleAuth()
    }

    const authUrl = this.buildGoogleAuthUrl()
    window.location.href = authUrl
  }

  /**
   * Constrói a URL de autenticação do Google
   */
  private buildGoogleAuthUrl(): string {
    const params = new URLSearchParams({
      client_id: this.googleClientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
      prompt: 'consent',
      state: this.generateState()
    })

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
  }

  /**
   * Gera um state aleatório para segurança
   */
  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15)
  }

  /**
   * Troca o código de autorização por token JWT no backend
   */
  async exchangeCodeForToken(code: string, state?: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/google', {
      code,
      redirect_uri: this.redirectUri,
      state
    })
    return response.data
  }

  /**
   * Método alternativo usando popup (fallback)
   */
  renderGoogleButton(element: HTMLElement, _options?: any): void {
    if (!this.isGoogleInitialized) {
      console.error('Google Auth not initialized')
      return
    }

    // Criar botão customizado que inicia o fluxo de redirect
    element.innerHTML = `
      <button 
        type="button"
        class="w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
      >
        <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path fill="#FFFFFF" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#FFFFFF" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FFFFFF" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#FFFFFF" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continuar com Google
      </button>
    `

    const button = element.querySelector('button')
    if (button) {
      button.addEventListener('click', () => {
        this.startGoogleAuth()
      })
    }
  }

  async getCurrentUser(): Promise<User> {
    // Backend não tem endpoint /auth/me
    // Vamos usar os dados salvos no localStorage
    const userData = localStorage.getItem('user')
    if (userData) {
      return JSON.parse(userData)
    }
    throw new Error('Usuário não encontrado')
  }

  async refreshToken(): Promise<AuthResponse> {
    // Backend não tem endpoint de refresh
    throw new Error('Refresh token não implementado no backend')
  }

  logout(): void {
    // Limpar localStorage
    localStorage.removeItem('jwt')
  }

  // Método para obter informações do usuário do Google (para debug)
  async getGoogleUserInfo(accessToken: string): Promise<GoogleUserInfo> {
    const response = await fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${accessToken}`)
    if (!response.ok) {
      throw new Error('Failed to get Google user info')
    }
    return response.json()
  }
}

export const authService = new AuthService()